import { useEffect, useState, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../store/hooks'
import { fetchPrescriptionList } from '../store/prescriptionListSlice'
import { fetchPatients } from '../store/patientListSlice'
import { fetchMedications } from '../store/medicationListSlice'
import { fetchVeterinarians } from '../store/settingsSlice'
import { PrescriptionListTable } from '../components/prescriptions/PrescriptionListTable'
import { PrescriptionSearch } from '../components/prescriptions/PrescriptionSearch'
import { NewPrescriptionPageModal } from '../components/prescriptions/NewPrescriptionPageModal'
import { Plus } from 'lucide-react'

export function PrescriptionsPage() {
  const dispatch = useAppDispatch()
  const { prescriptions, loading } = useAppSelector((s) => s.prescriptionList)
  const { patients } = useAppSelector((s) => s.patientList)
  const { medications } = useAppSelector((s) => s.medicationList)
  const { veterinarians } = useAppSelector((s) => s.settings)

  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    dispatch(fetchPrescriptionList())
    dispatch(fetchPatients())
    dispatch(fetchMedications())
    dispatch(fetchVeterinarians())
  }, [dispatch])

  const filteredPrescriptions = useMemo(() => {
    return prescriptions.filter((rx) => {
      // Search filter
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        const rxNumber = `RX-${String(rx.prescription_number).padStart(4, '0')}`.toLowerCase()
        if (
          !rx.patient_name.toLowerCase().includes(q) &&
          !rx.medication_name.toLowerCase().includes(q) &&
          !rxNumber.includes(q)
        ) {
          return false
        }
      }
      // Status filter
      if (statusFilter && rx.status !== statusFilter) {
        return false
      }
      return true
    })
  }, [prescriptions, searchQuery, statusFilter])

  const existingPrescriptions = useMemo(() => {
    return prescriptions.map((rx) => ({
      patient_id: rx.patient_id,
      medication_name: rx.medication_name,
      status: rx.status,
    }))
  }, [prescriptions])

  const handleCreatePrescription = async (data: {
    patient_id: string
    medication_id: string
    veterinarian_id: string
    dosage: string
    dosage_calculated: number | null
    dose_rate: number | null
    frequency: string
    route: string
    duration: string
    quantity_dispensed: number
    total_refills: number
    start_date: string
    end_date: string
    alerts: { type: string; severity: string; description: string; conflicting_drugs: string }[]
    controlled_substance: boolean
    dea_schedule: string | null
  }) => {
    // Create the prescription
    const res = await fetch('/.netlify/functions/prescriptions', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        patient_id: data.patient_id,
        medication_id: data.medication_id,
        veterinarian_id: data.veterinarian_id,
        dosage: data.dosage,
        frequency: data.frequency,
        route: data.route,
        duration: data.duration,
        quantity_dispensed: data.quantity_dispensed,
        total_refills: data.total_refills,
        start_date: data.start_date,
        end_date: data.end_date || null,
      }),
    })

    if (!res.ok) throw new Error('Failed to create prescription')
    const rx = await res.json()

    // Update dosage_calculated and dose_rate if set
    if (data.dosage_calculated != null || data.dose_rate != null) {
      await fetch(`/.netlify/functions/prescriptions/${rx.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dosage_calculated: data.dosage_calculated,
          dose_rate: data.dose_rate,
        }),
      })
    }

    // Create drug alerts if any
    for (const alert of data.alerts) {
      await fetch('/.netlify/functions/drug-alerts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prescription_id: rx.id,
          patient_id: data.patient_id,
          alert_type: alert.type,
          severity: alert.severity,
          description: alert.description,
          conflicting_drugs: alert.conflicting_drugs,
          acknowledged: true,
        }),
      })
    }

    // Create controlled substance log entry if needed
    if (data.controlled_substance && data.quantity_dispensed > 0) {
      const patient = patients.find((p) => p.id === data.patient_id)
      const vet = veterinarians.find((v) => v.id === data.veterinarian_id)
      await fetch('/.netlify/functions/controlled-substances', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          medication_id: data.medication_id,
          patient_id: data.patient_id,
          prescription_id: rx.id,
          transaction_type: 'Dispensed',
          quantity: data.quantity_dispensed,
          performed_by: vet?.name || 'Unknown',
          notes: `Prescription ${rx.prescription_number ? `RX-${String(rx.prescription_number).padStart(4, '0')}` : ''} for ${patient?.name || 'Unknown'}`,
        }),
      })
    }

    setModalOpen(false)
    dispatch(fetchPrescriptionList())
  }

  if (loading) {
    return (
      <div className="p-6 max-sm:p-3" data-testid="prescriptions-page">
        <p className="text-[13px] text-text-muted">Loading prescriptions...</p>
      </div>
    )
  }

  return (
    <div className="p-6 max-sm:p-3" data-testid="prescriptions-page">
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-[16px] font-semibold text-text-primary">Prescriptions</h1>
        <button
          data-testid="new-prescription-button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-1.5 h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] hover:bg-accent-dark transition-colors duration-100 cursor-pointer"
        >
          <Plus size={14} />
          New Prescription
        </button>
      </div>

      {/* Search and filters */}
      <div className="mb-4">
        <PrescriptionSearch
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <PrescriptionListTable prescriptions={filteredPrescriptions} />
      </div>

      {/* New Prescription Modal */}
      {modalOpen && (
        <NewPrescriptionPageModal
          patients={patients}
          medications={medications}
          veterinarians={veterinarians}
          existingPrescriptions={existingPrescriptions}
          onSubmit={handleCreatePrescription}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  )
}
